import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import Select, { components } from 'react-select';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import AgencyOnePageReportController from 'pages/Controller/AgencyOnePageReportController';

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (base) => ({
    ...base,
    minHeight: '38px',
    height: '38px',
    backgroundColor: '#fff',
    borderColor: '#c4c4c4',
    borderRadius: '4px',
    boxShadow: 'none',
    '&:hover': {
      borderColor: '#999'
    }
  }),
  valueContainer: (base) => ({
    ...base,
    height: '38px',
    padding: '0 8px'
  }),
  input: (base) => ({
    ...base,
    margin: '0px'
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: '38px'
  }),
  menu: (base) => ({
    ...base,
    zIndex: 1500,
    backgroundColor: '#fff'
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? '#f5f5f5' : '#fff',
    color: '#111',
    '&:active': { backgroundColor: '#eeeeee' }
  })
};

const CheckboxOption = (props) => (
  <components.Option {...props}>
    <input type="checkbox" checked={props.isSelected} readOnly style={{ marginRight: 8 }} />
    {props.label}
  </components.Option>
);
const multiSelectProps = (options, value, setValue, placeholder) => ({
  options, value, onChange: setValue, isMulti: true, closeMenuOnSelect: false,
  hideSelectedOptions: false, components: { Option: CheckboxOption, MultiValue: () => null },
  styles: selectStyles, menuPortalTarget: document.body, placeholder,
  isClearable: true, controlShouldRenderValue: false,
  noOptionsMessage: () => 'No options'
});

const cyanHeaderStyle = {
  backgroundColor: '#d32f2f',
  color: '#ffffff',
  fontWeight: 700,
  fontSize: '11px',
  padding: '6px 8px',
  border: '1px solid #b71c1c',
  whiteSpace: 'nowrap',
  lineHeight: 1.2
};

const amberHeaderStyle = {
  backgroundColor: '#d32f2f',
  color: '#ffffff',
  fontWeight: 700,
  fontSize: '11px',
  padding: '6px 8px',
  border: '1px solid #b71c1c',
  whiteSpace: 'nowrap',
  lineHeight: 1.2
};

const bodyCellStyle = (isNegative = false, isPositive = false) => ({
  fontSize: '11px',
  padding: '5px 8px',
  border: '1px solid #e0e0e0',
  color: isNegative ? '#d32f2f' : isPositive ? '#2e7d32' : '#222',
  fontWeight: isNegative || isPositive ? 600 : 500,
  whiteSpace: 'nowrap',
  lineHeight: 1.2
});

const totalCellStyle = (isNegative = false, isPositive = false) => ({
  fontSize: '11.5px',
  padding: '6px 8px',
  border: '1px solid #ccc',
  color: isNegative ? '#d32f2f' : isPositive ? '#2e7d32' : '#000',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  backgroundColor: '#f8f9fa',
  lineHeight: 1.2
});

const AgencyOnePageReport = () => {
  const {
    selectedArea,
    setSelectedArea,
    selectedMonth,
    setSelectedMonth,
    selectedDiv,
    setSelectedDiv,
    sendDate,
    areaOptions,
    monthOptions,
    divOptions,
    fullYearData,
    fullYearTotal,
    aprAugData,
    aprAugTotal,
    aprAugStats,
    ytdBadges,
    ytdVolumeData,
    ytdVolumeTotal,
    ytdValueData,
    ytdValueTotal,
    dealerwiseData,
    dealerwiseTotal,
    paymentOsData,
    paymentOsStats
  } = AgencyOnePageReportController();

  return (
    <Box
      sx={{
        backgroundColor: '#f5f5f5',
        minHeight: '100vh',
        p: { xs: 1.5, sm: 2.5 },
        pt: { xs: 3, sm: 4 },
        fontFamily: "'Roboto', 'Segoe UI', sans-serif"
      }}
    >
      <Paper
        elevation={2}
        sx={{
          maxWidth: '1400px',
          margin: '0 auto',
          p: { xs: 1.5, sm: 2.5 },
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          border: '1px solid #e0e0e0'
        }}
      >
        {/* ================= HEADER BAR ================= */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            gap: 1.5,
            mb: 2
          }}
        >
          {/* Left: Send date */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: '9px', color: '#777', textTransform: 'uppercase', lineHeight: 1 }}>
                last date
              </Typography>
              <Box
                sx={{
                  border: '1px solid #bbb',
                  backgroundColor: '#fff',
                  borderRadius: '3px',
                  px: 1,
                  py: 0.3,
                  mt: 0.2
                }}
              >
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#333', letterSpacing: '0.5px' }}>
                  {sendDate}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Center: Title */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '18px', sm: '22px', md: '25px' },
              color: '#1e293b',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              textAlign: 'center',
              flexGrow: 1,
              lineHeight: 1.2,
              overflow: 'visible'
            }}
          >
            AGENCY ONE PAGE REPORT
          </Typography>

        </Box>

        {/* ================= FILTER ROW ================= */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            mb: 2.5
          }}
        >
          <Box sx={{ width: { xs: '100%', sm: '180px' } }}>
            <Select
              {...multiSelectProps(areaOptions, selectedArea, setSelectedArea, `AREA (${selectedArea.length})`)}
            />
          </Box>

          <Box sx={{ width: { xs: '100%', sm: '180px' } }}>
            <Select
              {...multiSelectProps(monthOptions, selectedMonth, setSelectedMonth, `MONTH (${selectedMonth.length})`)}
            />
          </Box>

          <Box sx={{ width: { xs: '100%', sm: '180px' } }}>
            <Select
              {...multiSelectProps(divOptions, selectedDiv, setSelectedDiv, `DIV (${selectedDiv.length})`)}
            />
          </Box>

          {/* AREA Indicator Card */}
          <Box
            sx={{
              border: '1px solid #bbb',
              backgroundColor: '#fff',
              borderRadius: '4px',
              px: 2,
              py: 0.5,
              textAlign: 'center',
              minWidth: '130px',
              height: '38px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              ml: { sm: 1 }
            }}
          >
            <Typography sx={{ fontSize: '8.5px', color: '#777', textTransform: 'uppercase', lineHeight: 1 }}>
              AREA
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#222', lineHeight: 1.2 }}>
              {selectedArea?.value || 'WESTERN UP'}
            </Typography>
          </Box>
        </Box>

        {/* ================= SECTION 1: BUDGET VS ACHIEVEMENT ================= */}
        <Paper
          elevation={0}
          sx={{
            border: '2px solid #555',
            borderRadius: '10px',
            p: { xs: 1.5, sm: 2 },
            backgroundColor: '#ffffff',
            mb: 2.5
          }}
        >
          {/* Section Header */}
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '17px',
              textDecoration: 'underline',
              color: '#111',
              mb: 2,
              letterSpacing: '0.3px',
              textTransform: 'uppercase'
            }}
          >
            BUDGET VS ACHIEVEMENT
          </Typography>

          <Grid container spacing={2}>
            {/* Left Column: FULL YEAR */}
            <Grid item xs={12} lg={6}>
              <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '15px', color: '#111', textTransform: 'uppercase' }}>
                  FULL YEAR
                </Typography>
              </Box>

              <TableContainer sx={{ border: '1px solid #ccc', borderRadius: '4px', overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={cyanHeaderStyle}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                          DIV <ArrowDropUpIcon sx={{ fontSize: 16 }} />
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={cyanHeaderStyle}>
                        BUDGET<br />(MTR)
                      </TableCell>
                      <TableCell align="right" sx={cyanHeaderStyle}>
                        26-27<br />SALE MTR
                      </TableCell>
                      <TableCell align="right" sx={cyanHeaderStyle}>
                        BUDGET<br />(VAL)
                      </TableCell>
                      <TableCell align="right" sx={cyanHeaderStyle}>
                        26-27<br />SAL VAL
                      </TableCell>
                      <TableCell align="right" sx={cyanHeaderStyle}>
                        Bdgt %<br />(Vol)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fullYearData.map((row, idx) => (
                      <TableRow key={idx} hover sx={{ '&:nth-of-type(even)': { backgroundColor: '#fafafa' } }}>
                        <TableCell sx={bodyCellStyle()}>{row.div}</TableCell>
                        <TableCell align="right" sx={bodyCellStyle()}>{row.budgetMtr}</TableCell>
                        <TableCell align="right" sx={bodyCellStyle()}>{row.saleMtr}</TableCell>
                        <TableCell align="right" sx={bodyCellStyle()}>{row.budgetVal}</TableCell>
                        <TableCell align="right" sx={bodyCellStyle()}>{row.saleVal}</TableCell>
                        <TableCell align="right" sx={bodyCellStyle(true)}>{row.bdgtPct}</TableCell>
                      </TableRow>
                    ))}
                    {/* Grand Total Row */}
                    <TableRow>
                      <TableCell sx={totalCellStyle()}>{fullYearTotal.div}</TableCell>
                      <TableCell align="right" sx={totalCellStyle()}>{fullYearTotal.budgetMtr}</TableCell>
                      <TableCell align="right" sx={totalCellStyle()}>{fullYearTotal.saleMtr}</TableCell>
                      <TableCell align="right" sx={totalCellStyle()}>{fullYearTotal.budgetVal}</TableCell>
                      <TableCell align="right" sx={totalCellStyle()}>{fullYearTotal.saleVal}</TableCell>
                      <TableCell align="right" sx={totalCellStyle(true)}>{fullYearTotal.bdgtPct}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination footer */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 0.8, gap: 0.5 }}>
                <Typography sx={{ fontSize: '11px', color: '#555' }}>
                  1 - {fullYearData.length} / {fullYearData.length}
                </Typography>
              </Box>
            </Grid>

            {/* Right Column: APR-AUG with Green Border */}
            <Grid item xs={12} lg={6}>
              <Box
                sx={{
                  border: '2.5px solid #2e7d32',
                  borderRadius: '8px',
                  p: 1.5,
                  backgroundColor: '#ffffff'
                }}
              >
                {/* Top Metrics Row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1.5,
                    gap: 1
                  }}
                >
                  {/* Left Gauge Metric: Sale MTR */}
                  <Box sx={{ minWidth: '130px' }}>
                    <Typography sx={{ fontSize: '10px', color: '#666', textTransform: 'uppercase', fontWeight: 600 }}>
                      {aprAugStats.saleMtr.label}
                    </Typography>
                    <Typography sx={{ fontSize: '22px', fontWeight: 800, color: '#111', lineHeight: 1.1 }}>
                      {aprAugStats.saleMtr.value}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#d32f2f', lineHeight: 1.2 }}>
                      {aprAugStats.saleMtr.percentage}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                      <Box
                        sx={{
                          width: '90px',
                          height: '9px',
                          backgroundColor: '#e5e7eb',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}
                      >
                        <Box
                          sx={{
                            width: `${aprAugStats.saleMtr.progress}%`,
                            height: '100%',
                            backgroundColor: '#ef4444',
                            borderRadius: '4px'
                          }}
                        />
                      </Box>
                      <Typography sx={{ fontSize: '9px', color: '#666' }}>
                        {aprAugStats.saleMtr.target}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Center Label: APR-AUG */}
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: '16px',
                      color: '#111',
                      textTransform: 'uppercase',
                      textAlign: 'center'
                    }}
                  >
                    APR-AUG
                  </Typography>

                  {/* Right Gauge Metric: Sal Val */}
                  <Box sx={{ minWidth: '130px', textAlign: 'right' }}>
                    <Typography sx={{ fontSize: '10px', color: '#666', textTransform: 'uppercase', fontWeight: 600 }}>
                      {aprAugStats.saleVal.label}
                    </Typography>
                    <Typography sx={{ fontSize: '22px', fontWeight: 800, color: '#111', lineHeight: 1.1 }}>
                      {aprAugStats.saleVal.value}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#d32f2f', lineHeight: 1.2 }}>
                      {aprAugStats.saleVal.percentage}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 0.3 }}>
                      <Box
                        sx={{
                          width: '90px',
                          height: '9px',
                          backgroundColor: '#e5e7eb',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}
                      >
                        <Box
                          sx={{
                            width: `${aprAugStats.saleVal.progress}%`,
                            height: '100%',
                            backgroundColor: '#ef4444',
                            borderRadius: '4px'
                          }}
                        />
                      </Box>
                      <Typography sx={{ fontSize: '9px', color: '#666' }}>
                        {aprAugStats.saleVal.target}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* APR-AUG Table */}
                <TableContainer sx={{ border: '1px solid #ccc', borderRadius: '4px', overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={cyanHeaderStyle}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                            DIV <ArrowDropUpIcon sx={{ fontSize: 16 }} />
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={cyanHeaderStyle}>
                          BUDGET<br />(MTR)
                        </TableCell>
                        <TableCell align="right" sx={cyanHeaderStyle}>
                          26-27<br />SALE MTR
                        </TableCell>
                        <TableCell align="right" sx={cyanHeaderStyle}>
                          Bdgt %<br />(Vol)
                        </TableCell>
                        <TableCell align="right" sx={cyanHeaderStyle}>
                          BUDGET<br />(VAL)
                        </TableCell>
                        <TableCell align="right" sx={cyanHeaderStyle}>
                          26-27<br />SAL VAL
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {aprAugData.map((row, idx) => (
                        <TableRow key={idx} hover sx={{ '&:nth-of-type(even)': { backgroundColor: '#fafafa' } }}>
                          <TableCell sx={bodyCellStyle()}>{row.div}</TableCell>
                          <TableCell align="right" sx={bodyCellStyle()}>{row.budgetMtr}</TableCell>
                          <TableCell align="right" sx={bodyCellStyle()}>{row.saleMtr}</TableCell>
                          <TableCell align="right" sx={bodyCellStyle(true)}>{row.bdgtPct}</TableCell>
                          <TableCell align="right" sx={bodyCellStyle()}>{row.budgetVal}</TableCell>
                          <TableCell align="right" sx={bodyCellStyle()}>{row.saleVal}</TableCell>
                        </TableRow>
                      ))}
                      {/* Grand Total Row */}
                      <TableRow>
                        <TableCell sx={totalCellStyle()}>{aprAugTotal.div}</TableCell>
                        <TableCell align="right" sx={totalCellStyle()}>{aprAugTotal.budgetMtr}</TableCell>
                        <TableCell align="right" sx={totalCellStyle()}>{aprAugTotal.saleMtr}</TableCell>
                        <TableCell align="right" sx={totalCellStyle(true)}>{aprAugTotal.bdgtPct}</TableCell>
                        <TableCell align="right" sx={totalCellStyle()}>{aprAugTotal.budgetVal}</TableCell>
                        <TableCell align="right" sx={totalCellStyle()}>{aprAugTotal.saleVal}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination footer */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 0.8, gap: 0.5 }}>
                  <Typography sx={{ fontSize: '11px', color: '#555' }}>
                    1 - {aprAugData.length} / {aprAugData.length}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* ================= SECTION 2: YTD GROWTH OVER LAST YEAR ================= */}
        <Paper
          elevation={0}
          sx={{
            border: '2px solid #555',
            borderRadius: '10px',
            p: { xs: 1.5, sm: 2 },
            backgroundColor: '#ffffff'
          }}
        >
          {/* Section Header */}
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '17px',
              textDecoration: 'underline',
              color: '#111',
              mb: 2,
              letterSpacing: '0.3px',
              textTransform: 'uppercase',
              textAlign: 'center'
            }}
          >
            YTD GROWTH OVER LAST YEAR
          </Typography>

          {/* Badges Container */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 3, sm: 6 },
              mb: 2.5
            }}
          >
            {/* Pill 1: Volume Growth */}
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  border: '1.5px solid #333',
                  borderRadius: '30px',
                  p: '3px',
                  display: 'inline-block',
                  backgroundColor: '#fff'
                }}
              >
                <Typography sx={{ fontSize: '11.5px', fontWeight: 700, color: '#111', px: 2, pt: 0.3 }}>
                  {ytdBadges.volume.label}
                </Typography>
                <Box
                  sx={{
                    backgroundColor: '#1b7d34',
                    color: '#fff',
                    borderRadius: '25px',
                    px: { xs: 4, sm: 7 },
                    py: 0.4,
                    mt: 0.3
                  }}
                >
                  <Typography sx={{ fontSize: '16px', fontWeight: 800, letterSpacing: '0.5px' }}>
                    {ytdBadges.volume.value}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Pill 2: Value Growth */}
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  border: '1.5px solid #333',
                  borderRadius: '30px',
                  p: '3px',
                  display: 'inline-block',
                  backgroundColor: '#fff'
                }}
              >
                <Typography sx={{ fontSize: '11.5px', fontWeight: 700, color: '#111', px: 2, pt: 0.3 }}>
                  {ytdBadges.value.label}
                </Typography>
                <Box
                  sx={{
                    backgroundColor: '#1b7d34',
                    color: '#fff',
                    borderRadius: '25px',
                    px: { xs: 4, sm: 7 },
                    py: 0.4,
                    mt: 0.3
                  }}
                >
                  <Typography sx={{ fontSize: '16px', fontWeight: 800, letterSpacing: '0.5px' }}>
                    {ytdBadges.value.value}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Grid container spacing={2}>
            {/* Left Table: Volume (MTR) */}
            <Grid item xs={12} lg={6}>
              <TableContainer sx={{ border: '1px solid #ccc', borderRadius: '4px', overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={amberHeaderStyle}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                          DIV <ArrowDropUpIcon sx={{ fontSize: 16 }} />
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={amberHeaderStyle}>
                        24-25 SALE<br />MTR
                      </TableCell>
                      <TableCell align="right" sx={amberHeaderStyle}>
                        25-26 SALE<br />MTR
                      </TableCell>
                      <TableCell align="right" sx={amberHeaderStyle}>
                        26-27 SALE<br />MTR
                      </TableCell>
                      <TableCell align="right" sx={amberHeaderStyle}>
                        LY Growth<br />% (Vol)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ytdVolumeData.map((row, idx) => {
                      const isNegative = String(row.lyGrowthPct).includes('-');
                      return (
                        <TableRow key={idx} hover sx={{ '&:nth-of-type(even)': { backgroundColor: '#fafafa' } }}>
                          <TableCell sx={bodyCellStyle()}>{row.div}</TableCell>
                          <TableCell align="right" sx={bodyCellStyle()}>{row.saleMtr2425}</TableCell>
                          <TableCell align="right" sx={bodyCellStyle()}>{row.saleMtr2526}</TableCell>
                          <TableCell align="right" sx={bodyCellStyle()}>{row.saleMtr2627}</TableCell>
                          <TableCell align="right" sx={bodyCellStyle(isNegative, !isNegative)}>
                            {row.lyGrowthPct}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Grand Total Row */}
                    <TableRow>
                      <TableCell sx={totalCellStyle()}>{ytdVolumeTotal.div}</TableCell>
                      <TableCell align="right" sx={totalCellStyle()}>{ytdVolumeTotal.saleMtr2425}</TableCell>
                      <TableCell align="right" sx={totalCellStyle()}>{ytdVolumeTotal.saleMtr2526}</TableCell>
                      <TableCell align="right" sx={totalCellStyle()}>{ytdVolumeTotal.saleMtr2627}</TableCell>
                      <TableCell align="right" sx={totalCellStyle()}>{ytdVolumeTotal.lyGrowthPct}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination footer */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 0.8, gap: 0.5 }}>
                <Typography sx={{ fontSize: '11px', color: '#555' }}>
                  1 - {ytdVolumeData.length} / {ytdVolumeData.length}
                </Typography>
              </Box>
            </Grid>

            {/* Right Table: Value (VAL) */}
            <Grid item xs={12} lg={6}>
              <TableContainer sx={{ border: '1px solid #ccc', borderRadius: '4px', overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={amberHeaderStyle}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                          DIV <ArrowDropUpIcon sx={{ fontSize: 16 }} />
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={amberHeaderStyle}>
                        24-25<br />SAL VAL
                      </TableCell>
                      <TableCell align="right" sx={amberHeaderStyle}>
                        25-26<br />SAL VAL
                      </TableCell>
                      <TableCell align="right" sx={amberHeaderStyle}>
                        26-27<br />SAL VAL
                      </TableCell>
                      <TableCell align="right" sx={amberHeaderStyle}>
                        LY Growth %<br />(Val)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ytdValueData.map((row, idx) => {
                      const isNegative = String(row.lyGrowthPct).includes('-');
                      return (
                        <TableRow key={idx} hover sx={{ '&:nth-of-type(even)': { backgroundColor: '#fafafa' } }}>
                          <TableCell sx={bodyCellStyle()}>{row.div}</TableCell>
                          <TableCell align="right" sx={bodyCellStyle()}>{row.salVal2425}</TableCell>
                          <TableCell align="right" sx={bodyCellStyle()}>{row.salVal2526}</TableCell>
                          <TableCell align="right" sx={bodyCellStyle()}>{row.salVal2627}</TableCell>
                          <TableCell align="right" sx={bodyCellStyle(isNegative, !isNegative)}>
                            {row.lyGrowthPct}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Grand Total Row */}
                    <TableRow>
                      <TableCell sx={totalCellStyle()}>{ytdValueTotal.div}</TableCell>
                      <TableCell align="right" sx={totalCellStyle()}>{ytdValueTotal.salVal2425}</TableCell>
                      <TableCell align="right" sx={totalCellStyle()}>{ytdValueTotal.salVal2526}</TableCell>
                      <TableCell align="right" sx={totalCellStyle()}>{ytdValueTotal.salVal2627}</TableCell>
                      <TableCell align="right" sx={totalCellStyle()}>{ytdValueTotal.lyGrowthPct}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination footer */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 0.8, gap: 0.5 }}>
                <Typography sx={{ fontSize: '11px', color: '#555' }}>
                  1 - {ytdValueData.length} / {ytdValueData.length}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* ================= DEALERWISE PERFORMANCE ================= */}
        <Paper
          elevation={0}
          sx={{
            border: '2px solid #555',
            borderRadius: '10px',
            p: { xs: 1.5, sm: 2 },
            backgroundColor: '#ffffff',
            mb: 2.5
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: '17px', textAlign: 'center', mb: 1.5 }}>
            DEALERWISE PERFORMANCE
          </Typography>
          <Box sx={{ width: { xs: '100%', sm: '180px' }, mb: 1.5 }}>
            {/* Area filter temporarily disabled for Dealerwise Performance. */}
          </Box>
          <TableContainer sx={{ border: '1px solid #222', borderRadius: '3px', overflowX: 'auto' }}>
            <Table size="small">
              <TableHead><TableRow>
                {['Sold-to-Party Name', 'CITY', '26-28 FULL YEAR', '26-27 VAL', 'YTD 26-28', 'YTD 26-27', 'YTD Growth%'].map((header) => <TableCell key={header} align={header === 'Sold-to-Party Name' || header === 'CITY' ? 'left' : 'right'} sx={cyanHeaderStyle}>{header}</TableCell>)}
              </TableRow></TableHead>
              <TableBody>
                {dealerwiseData.map((row) => <TableRow key={row.id} hover>
                  <TableCell sx={bodyCellStyle()}>{row.partyName}</TableCell><TableCell sx={bodyCellStyle()}>{row.city}</TableCell>
                  <TableCell align="right" sx={bodyCellStyle()}>{row.fullYear}</TableCell><TableCell align="right" sx={bodyCellStyle()}>{row.val2627}</TableCell>
                  <TableCell align="right" sx={bodyCellStyle()}>{row.ytd2628}</TableCell><TableCell align="right" sx={bodyCellStyle()}>{row.ytd2627}</TableCell><TableCell align="right" sx={bodyCellStyle()}>{row.growthPct}</TableCell>
                </TableRow>)}
                <TableRow><TableCell colSpan={2} sx={totalCellStyle()}>{dealerwiseTotal.partyName}</TableCell><TableCell align="right" sx={totalCellStyle()}>{dealerwiseTotal.fullYear}</TableCell><TableCell align="right" sx={totalCellStyle()}>{dealerwiseTotal.val2627}</TableCell><TableCell align="right" sx={totalCellStyle()}>{dealerwiseTotal.ytd2628}</TableCell><TableCell align="right" sx={totalCellStyle()}>{dealerwiseTotal.ytd2627}</TableCell><TableCell align="right" sx={totalCellStyle()}>{dealerwiseTotal.growthPct}</TableCell></TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* ================= PAYMENT O/S ================= */}
        <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: '4px', p: { xs: 1.5, sm: 2 }, backgroundColor: '#ffffff' }}>
          <Typography sx={{ fontWeight: 800, fontSize: '17px', textAlign: 'center' }}>Payment &amp; O/s</Typography>
          <Typography sx={{ fontSize: '11px', color: '#666', textAlign: 'center', mb: 1.5 }}>Last Update<br /><b>Sep 3, 2026</b></Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, border: '1px solid #777', borderRadius: '30px', p: 1.5, mb: 1.5, textAlign: 'center' }}>
            {[["G Amt", paymentOsStats.gAmt], ["NET Amt", paymentOsStats.netAmt], ["L47", paymentOsStats.l47], ["M90", paymentOsStats.m90], ["M47", paymentOsStats.m47], ["L60", paymentOsStats.l60], ["M60", paymentOsStats.m60]].map(([label, value]) => <Box key={label}><Typography sx={{ fontSize: '9px', color: '#777' }}>{label}</Typography><Typography sx={{ fontSize: '16px' }}>{value}</Typography></Box>)}
          </Box>
          <TableContainer sx={{ border: '1px solid #222', borderRadius: '3px', overflowX: 'auto' }}><Table size="small">
            <TableHead><TableRow>{['Sold-to-Party Name', 'Sold-to-Party', 'G Amt', 'NET Amt', 'L47', 'M47', 'M90', 'M120', 'M180', 'BEFORE MAR25'].map((header) => <TableCell key={header} sx={cyanHeaderStyle}>{header}</TableCell>)}</TableRow></TableHead>
            <TableBody>{paymentOsData.map((row) => <TableRow key={row.partyCode}><TableCell sx={bodyCellStyle()}>{row.partyName}</TableCell><TableCell sx={bodyCellStyle()}>{row.partyCode}</TableCell>{['gAmt', 'netAmt', 'l47', 'm47', 'm90', 'm120', 'm180', 'beforeMar25'].map((key) => <TableCell key={key} align="right" sx={bodyCellStyle()}>{row[key]}</TableCell>)}</TableRow>)}</TableBody>
          </Table></TableContainer>
        </Paper>
      </Paper>
    </Box>
  );
};

export default AgencyOnePageReport;
